import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { authenticate, authenticateWithServiceNumber } from "../actions/authActions";
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Link,
  Alert,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  Badge,
  Lock,
  Visibility,
  VisibilityOff,
  ArrowForward,
  PersonAdd
} from "@mui/icons-material";
import { useTheme, useMediaQuery } from "@mui/material";

// Import your background images
import desktopBackground from "../assets/homd.png"; // Desktop background
import mobileBackground from "../assets/hom.gif"; // Mobile background - replace with your mobile image

const LoginPage = () => {
  const [phoneNo, setPhoneNumber] = useState("");
  const [loginMethod, setLoginMethod] = useState("phone");
  const [serviceNo, setServiceNo] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  
  // Check for mobile view
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const storedPhoneNumber = localStorage.getItem("phoneNumber");
    const storedOtp = localStorage.getItem("sentOtp");
    const storedUserData = localStorage.getItem("userData");
    const storedAuthKey = localStorage.getItem("authKey");
    const storedServiceNo = localStorage.getItem("serviceNo");
    
    // Check service number login
    if (isAuthenticated && storedServiceNo && storedAuthKey && storedUserData) {
      navigate("/home");
    }
    // Check phone login
    else if (isAuthenticated && storedPhoneNumber && storedOtp && storedUserData) {
      navigate("/home");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async () => {
    if (loginMethod === "phone") {
      const trimmedPhoneNumber = phoneNo.trim();
      
      if (!trimmedPhoneNumber) {
        alert("Please enter a phone number.");
        return;
      }

      if (trimmedPhoneNumber.length < 10) {
        alert("Please enter a valid phone number.");
        return;
      }
    
      const result = await dispatch(authenticate(trimmedPhoneNumber));
      
      if (result.success) {
        navigate("/verify", { 
          state: { 
            phoneNumber: trimmedPhoneNumber,
            sentOtp: result.data 
          }
        });
      } else {
        alert(result.error?.message || "Authentication failed. Please try again.");
      }
    } else {
      const trimmedServiceNo = serviceNo.trim();
      const trimmedPassword = password.trim();
      
      if (!trimmedServiceNo) {
        alert("Please enter your service number.");
        return;
      }
      
      if (!trimmedPassword) {
        alert("Please enter your password.");
        return;
      }
      
      const result = await dispatch(authenticateWithServiceNumber(trimmedServiceNo, trimmedPassword));
      
      if (result.success) {
        navigate("/home");
      } else {
        alert(result.error?.message || "Invalid service number or password. Please try again.");
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#F8F9FA",
        // Conditional background image based on screen size
        backgroundImage: isMobile 
          ? `url(${mobileBackground})` 
          : `url(${desktopBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed", // Optional: makes background fixed on scroll
        padding: 2,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        // Optional: Add a fallback background color
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.1)", // Optional overlay
          zIndex: -1,
        }
      }}
      maxWidth={false} // Remove max width constraint
    >
      <Paper
        sx={{
          borderRadius: 5,
          boxShadow: 8,
          padding: { xs: 3, sm: 4 },
          maxWidth: 400,
          width: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)", // Optional: adds blur effect to background
          // Optional: Different styling for mobile
          ...(isMobile && {
            margin: 1,
            padding: 2,
            borderRadius: 3,
          })
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: "#000" }}>
            Welcome..!
          </Typography>
          <Typography variant="body2" sx={{ color: "#666" }}>
            Sign in to continue to your account
          </Typography>
        </Box>

        {/* Login Method Toggle */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            p: 0.5,
            mb: 3,
            display: "flex",
            background: "#f8f9fa"
          }}
        >
          <Box
            onClick={() => setLoginMethod("phone")}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              padding: "8px",
              borderRadius: "6px",
              background: loginMethod === "phone" ? "#1e0153" : "transparent",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            <Phone sx={{ 
              fontSize: 18, 
              color: loginMethod === "phone" ? "#fff" : "#666"
            }} />
            <Typography
              sx={{
                color: loginMethod === "phone" ? "#fff" : "#666",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              Phone
            </Typography>
          </Box>

          <Box
            onClick={() => setLoginMethod("service")}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              padding: "8px",
              borderRadius: "6px",
              background: loginMethod === "service" ? "#1e0153" : "transparent",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            <Badge sx={{ 
              fontSize: 18, 
              color: loginMethod === "service" ? "#fff" : "#666"
            }} />
            <Typography
              sx={{
                color: loginMethod === "service" ? "#fff" : "#666",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              Service No
            </Typography>
          </Box>
        </Paper>

        {/* Form */}
        <Box sx={{ mb: 3 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2, 
                borderRadius: 1,
                "& .MuiAlert-icon": { fontSize: 20 }
              }}
            >
              {error.message || "Authentication failed. Please try again."}
            </Alert>
          )}

          {loginMethod === "phone" ? (
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter mobile number"
                value={phoneNo}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setPhoneNumber(value);
                }}
                onKeyPress={handleKeyPress}
                type="tel"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ fontSize: 18, color: "#666" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    "&:hover fieldset": {
                      borderColor: "#000",
                    },
                  },
                }}
              />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Service Number"
                value={serviceNo}
                onChange={(e) => setServiceNo(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Badge sx={{ fontSize: 18, color: "#666" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    "&:hover fieldset": {
                      borderColor: "#000",
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                size="small"
                placeholder="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ fontSize: 18, color: "#666" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                        size="small"
                        sx={{ p: 0.5 }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    "&:hover fieldset": {
                      borderColor: "#000",
                    },
                  },
                }}
              />
            </Box>
          )}

          {/* Remember Me Checkbox - Only for Service No login */}
          {loginMethod === "service" && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{ p: 0.5, mr: 1 }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Remember me
                </Typography>
              }
              sx={{ mb: 2 }}
            />
          )}

          {/* Login Button */}
          <LoadingButton
            onClick={handleSubmit}
            loading={loading}
            variant="contained"
            fullWidth
            size="medium"
            endIcon={<ArrowForward fontSize="small" />}
            sx={{
              bgcolor: "#1e0153",
              color: "#fff",
              borderRadius: 1,
              py: 1,
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                bgcolor: "#333",
              },
              "&.Mui-disabled": {
                bgcolor: "#e0e0e0",
                color: "#999",
              },
            }}
          >
            Sign In
          </LoadingButton>
        </Box>

        {/* Alternative Actions */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 2,
            mb: 3
          }}
        >
          <Typography variant="body2" sx={{ color: "#666", mb: 1, textAlign: "center" }}>
            Don't have an account?
          </Typography>
          <Box
            onClick={() => navigate("/register")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              padding: "10px",
              borderRadius: 1,
              background: "#f8f9fa",
              border: "1px solid #e0e0e0",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "#f0f0f0",
                borderColor: "#000",
              },
            }}
          >
            <PersonAdd sx={{ fontSize: 18, color: "#666" }} />
            <Typography
              sx={{
                color: "#000",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              Create new account
            </Typography>
          </Box>
        </Paper>

        {/* Footer Links */}
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 0.5 }}>
            Having trouble signing in?
          </Typography>
          <Link
            sx={{
              color: "#666",
              fontSize: "0.75rem",
              cursor: "pointer",
              "&:hover": { color: "#000", textDecoration: "underline" }
            }}
          >
            Reset password
          </Link>
          <Typography variant="caption" sx={{ color: "#999", mx: 1 }}>•</Typography>
          <Link
            sx={{
              color: "#666",
              fontSize: "0.75rem",
              cursor: "pointer",
              "&:hover": { color: "#000", textDecoration: "underline" }
            }}
          >
            Contact support
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;





// import React, { useState, useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { authenticate, authenticateWithServiceNumber } from "../actions/authActions";
// import {
//   Box,
//   Container,
//   Typography,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Paper,
//   Link,
//   Alert,
//   FormControlLabel,
//   Checkbox
// } from "@mui/material";
// import LoadingButton from "@mui/lab/LoadingButton";
// import { useNavigate } from "react-router-dom";
// import {
//   Phone,
//   Badge,
//   Lock,
//   Visibility,
//   VisibilityOff,
//   ArrowForward,
//   PersonAdd
// } from "@mui/icons-material";

// // Import your background image
// import imge from "../assets/hom.gif";

// const LoginPage = () => {
//   const [phoneNo, setPhoneNumber] = useState("");
//   const [loginMethod, setLoginMethod] = useState("phone");
//   const [serviceNo, setServiceNo] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPassword, setShowPassword] = useState(false);
//   const [rememberMe, setRememberMe] = useState(false);
//   const dispatch = useDispatch();
//   const { loading, error, isAuthenticated } = useSelector((state) => state.auth);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const storedPhoneNumber = localStorage.getItem("phoneNumber");
//     const storedOtp = localStorage.getItem("sentOtp");
//     const storedUserData = localStorage.getItem("userData");
//     const storedAuthKey = localStorage.getItem("authKey");
//     const storedServiceNo = localStorage.getItem("serviceNo");
    
//     // Check service number login
//     if (isAuthenticated && storedServiceNo && storedAuthKey && storedUserData) {
//       navigate("/home");
//     }
//     // Check phone login
//     else if (isAuthenticated && storedPhoneNumber && storedOtp && storedUserData) {
//       navigate("/home");
//     }
//   }, [isAuthenticated, navigate]);

//   const handleSubmit = async () => {
//     if (loginMethod === "phone") {
//       const trimmedPhoneNumber = phoneNo.trim();
      
//       if (!trimmedPhoneNumber) {
//         alert("Please enter a phone number.");
//         return;
//       }

//       if (trimmedPhoneNumber.length < 10) {
//         alert("Please enter a valid phone number.");
//         return;
//       }
    
//       const result = await dispatch(authenticate(trimmedPhoneNumber));
      
//       if (result.success) {
//         navigate("/verify", { 
//           state: { 
//             phoneNumber: trimmedPhoneNumber,
//             sentOtp: result.data 
//           }
//         });
//       } else {
//         alert(result.error?.message || "Authentication failed. Please try again.");
//       }
//     } else {
//       const trimmedServiceNo = serviceNo.trim();
//       const trimmedPassword = password.trim();
      
//       if (!trimmedServiceNo) {
//         alert("Please enter your service number.");
//         return;
//       }
      
//       if (!trimmedPassword) {
//         alert("Please enter your password.");
//         return;
//       }
      
//       const result = await dispatch(authenticateWithServiceNumber(trimmedServiceNo, trimmedPassword));
      
//       if (result.success) {
//         navigate("/home");
//       } else {
//         alert(result.error?.message || "Invalid service number or password. Please try again.");
//       }
//     }
//   };

//   const handleKeyPress = (e) => {
//     if (e.key === 'Enter') {
//       handleSubmit();
//     }
//   };

//   const handleClickShowPassword = () => {
//     setShowPassword(!showPassword);
//   };

//   return (
//     <Container
//       sx={{
//         display: "flex",
//         flexDirection: "column",
//         justifyContent: "center",
//         alignItems: "center",
//         minHeight: "100vh",
//         backgroundColor: "#F8F9FA",
//         backgroundImage: `url(${imge})`,
//         backgroundSize: "cover",
//         backgroundPosition: "center",
//         backgroundRepeat: "no-repeat",
//         padding: 2,
//         position: "absolute",
//         top: 0,
//         left: 0,
//         right: 0,
//       }}
//     >
//       <Paper
//         sx={{
//           borderRadius: 5,
//           boxShadow: 8,
//           padding: { xs: 3, sm: 4 },
//           maxWidth: 400,
//           width: "100%",
//           backgroundColor: "rgba(255, 255, 255, 0.95)",
//         }}
//       >
//         {/* Header */}
//         <Box sx={{ mb: 4, textAlign: "center" }}>
//           <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: "#000" }}>
//             Welcome..!
//           </Typography>
//           <Typography variant="body2" sx={{ color: "#666" }}>
//             Sign in to continue to your account
//           </Typography>
//         </Box>

//         {/* Login Method Toggle */}
//         <Paper
//           elevation={0}
//           sx={{
//             border: "1px solid #e0e0e0",
//             borderRadius: 1,
//             p: 0.5,
//             mb: 3,
//             display: "flex",
//             background: "#f8f9fa"
//           }}
//         >
//           <Box
//             onClick={() => setLoginMethod("phone")}
//             sx={{
//               flex: 1,
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               gap: 1,
//               padding: "8px",
//               borderRadius: "6px",
//               background: loginMethod === "phone" ? "#1e0153" : "transparent",
//               cursor: "pointer",
//               transition: "all 0.2s ease",
//             }}
//           >
//             <Phone sx={{ 
//               fontSize: 18, 
//               color: loginMethod === "phone" ? "#fff" : "#666"
//             }} />
//             <Typography
//               sx={{
//                 color: loginMethod === "phone" ? "#fff" : "#666",
//                 fontSize: "0.875rem",
//                 fontWeight: 500,
//               }}
//             >
//               Phone
//             </Typography>
//           </Box>

//           <Box
//             onClick={() => setLoginMethod("service")}
//             sx={{
//               flex: 1,
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               gap: 1,
//               padding: "8px",
//               borderRadius: "6px",
//               background: loginMethod === "service" ? "#1e0153" : "transparent",
//               cursor: "pointer",
//               transition: "all 0.2s ease",
//             }}
//           >
//             <Badge sx={{ 
//               fontSize: 18, 
//               color: loginMethod === "service" ? "#fff" : "#666"
//             }} />
//             <Typography
//               sx={{
//                 color: loginMethod === "service" ? "#fff" : "#666",
//                 fontSize: "0.875rem",
//                 fontWeight: 500,
//               }}
//             >
//               Service No
//             </Typography>
//           </Box>
//         </Paper>

//         {/* Form */}
//         <Box sx={{ mb: 3 }}>
//           {error && (
//             <Alert 
//               severity="error" 
//               sx={{ 
//                 mb: 2, 
//                 borderRadius: 1,
//                 "& .MuiAlert-icon": { fontSize: 20 }
//               }}
//             >
//               {error.message || "Authentication failed. Please try again."}
//             </Alert>
//           )}

//           {loginMethod === "phone" ? (
//             <Box>
//               <TextField
//                 fullWidth
//                 size="small"
//                 placeholder="Enter mobile number"
//                 value={phoneNo}
//                 onChange={(e) => {
//                   const value = e.target.value.replace(/[^0-9]/g, '');
//                   setPhoneNumber(value);
//                 }}
//                 onKeyPress={handleKeyPress}
//                 type="tel"
//                 InputProps={{
//                   startAdornment: (
//                     <InputAdornment position="start">
//                       <Phone sx={{ fontSize: 18, color: "#666" }} />
//                     </InputAdornment>
//                   ),
//                 }}
//                 sx={{
//                   mb: 2,
//                   "& .MuiOutlinedInput-root": {
//                     fontSize: "0.875rem",
//                     "&:hover fieldset": {
//                       borderColor: "#000",
//                     },
//                   },
//                 }}
//               />
//             </Box>
//           ) : (
//             <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
//               <TextField
//                 fullWidth
//                 size="small"
//                 placeholder="Service Number"
//                 value={serviceNo}
//                 onChange={(e) => setServiceNo(e.target.value)}
//                 onKeyPress={handleKeyPress}
//                 InputProps={{
//                   startAdornment: (
//                     <InputAdornment position="start">
//                       <Badge sx={{ fontSize: 18, color: "#666" }} />
//                     </InputAdornment>
//                   ),
//                 }}
//                 sx={{
//                   "& .MuiOutlinedInput-root": {
//                     fontSize: "0.875rem",
//                     "&:hover fieldset": {
//                       borderColor: "#000",
//                     },
//                   },
//                 }}
//               />

//               <TextField
//                 fullWidth
//                 size="small"
//                 placeholder="Password"
//                 type={showPassword ? "text" : "password"}
//                 value={password}
//                 onChange={(e) => setPassword(e.target.value)}
//                 onKeyPress={handleKeyPress}
//                 InputProps={{
//                   startAdornment: (
//                     <InputAdornment position="start">
//                       <Lock sx={{ fontSize: 18, color: "#666" }} />
//                     </InputAdornment>
//                   ),
//                   endAdornment: (
//                     <InputAdornment position="end">
//                       <IconButton
//                         onClick={handleClickShowPassword}
//                         edge="end"
//                         size="small"
//                         sx={{ p: 0.5 }}
//                       >
//                         {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
//                       </IconButton>
//                     </InputAdornment>
//                   ),
//                 }}
//                 sx={{
//                   "& .MuiOutlinedInput-root": {
//                     fontSize: "0.875rem",
//                     "&:hover fieldset": {
//                       borderColor: "#000",
//                     },
//                   },
//                 }}
//               />
//             </Box>
//           )}

//           {/* Remember Me Checkbox - Only for Service No login */}
//           {loginMethod === "service" && (
//             <FormControlLabel
//               control={
//                 <Checkbox
//                   size="small"
//                   checked={rememberMe}
//                   onChange={(e) => setRememberMe(e.target.checked)}
//                   sx={{ p: 0.5, mr: 1 }}
//                 />
//               }
//               label={
//                 <Typography variant="caption" sx={{ color: "#666" }}>
//                   Remember me
//                 </Typography>
//               }
//               sx={{ mb: 2 }}
//             />
//           )}

//           {/* Login Button */}
//           <LoadingButton
//             onClick={handleSubmit}
//             loading={loading}
//             variant="contained"
//             fullWidth
//             size="medium"
//             endIcon={<ArrowForward fontSize="small" />}
//             sx={{
//               bgcolor: "#1e0153",
//               color: "#fff",
//               borderRadius: 1,
//               py: 1,
//               fontSize: "0.875rem",
//               fontWeight: 500,
//               "&:hover": {
//                 bgcolor: "#333",
//               },
//               "&.Mui-disabled": {
//                 bgcolor: "#e0e0e0",
//                 color: "#999",
//               },
//             }}
//           >
//             Sign In
//           </LoadingButton>
//         </Box>

//         {/* Alternative Actions */}
//         <Paper
//           elevation={0}
//           sx={{
//             border: "1px solid #e0e0e0",
//             borderRadius: 2,
//             p: 2,
//             mb: 3
//           }}
//         >
//           <Typography variant="body2" sx={{ color: "#666", mb: 1, textAlign: "center" }}>
//             Don't have an account?
//           </Typography>
//           <Box
//             onClick={() => navigate("/register")}
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               gap: 1,
//               padding: "10px",
//               borderRadius: 1,
//               background: "#f8f9fa",
//               border: "1px solid #e0e0e0",
//               cursor: "pointer",
//               transition: "all 0.2s ease",
//               "&:hover": {
//                 background: "#f0f0f0",
//                 borderColor: "#000",
//               },
//             }}
//           >
//             <PersonAdd sx={{ fontSize: 18, color: "#666" }} />
//             <Typography
//               sx={{
//                 color: "#000",
//                 fontSize: "0.875rem",
//                 fontWeight: 500,
//               }}
//             >
//               Create new account
//             </Typography>
//           </Box>
//         </Paper>

//         {/* Footer Links */}
//         <Box sx={{ textAlign: "center" }}>
//           <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 0.5 }}>
//             Having trouble signing in?
//           </Typography>
//           <Link
//             sx={{
//               color: "#666",
//               fontSize: "0.75rem",
//               cursor: "pointer",
//               "&:hover": { color: "#000", textDecoration: "underline" }
//             }}
//           >
//             Reset password
//           </Link>
//           <Typography variant="caption" sx={{ color: "#999", mx: 1 }}>•</Typography>
//           <Link
//             sx={{
//               color: "#666",
//               fontSize: "0.75rem",
//               cursor: "pointer",
//               "&:hover": { color: "#000", textDecoration: "underline" }
//             }}
//           >
//             Contact support
//           </Link>
//         </Box>
//       </Paper>
//     </Container>
//   );
// };

// export default LoginPage;























// import React, { useState, useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { authenticate, authenticateWithServiceNumber } from "../actions/authActions";
// import {
//   Box,
//   Container,
//   Typography,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Paper,
//   Link,
//   Alert,
//   FormControlLabel,
//   Checkbox
// } from "@mui/material";
// import LoadingButton from "@mui/lab/LoadingButton";
// import { useNavigate } from "react-router-dom";
// import {
//   Phone,
//   Badge,
//   Lock,
//   Visibility,
//   VisibilityOff,
//   ArrowForward,
//   PersonAdd
// } from "@mui/icons-material";

// const LoginPage = () => {
//   const [phoneNo, setPhoneNumber] = useState("");
//   const [loginMethod, setLoginMethod] = useState("phone");
//   const [serviceNo, setServiceNo] = useState("");
//   const [password, setPassword] = useState("");
//   const [showPassword, setShowPassword] = useState(false);
//   const [rememberMe, setRememberMe] = useState(false);
//   const dispatch = useDispatch();
//   const { loading, error, isAuthenticated } = useSelector((state) => state.auth);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const storedPhoneNumber = localStorage.getItem("phoneNumber");
//     const storedOtp = localStorage.getItem("sentOtp");
//     const storedUserData = localStorage.getItem("userData");
//     const storedAuthKey = localStorage.getItem("authKey");
//     const storedServiceNo = localStorage.getItem("serviceNo");
    
//     // Check service number login
//     if (isAuthenticated && storedServiceNo && storedAuthKey && storedUserData) {
//       navigate("/home");
//     }
//     // Check phone login
//     else if (isAuthenticated && storedPhoneNumber && storedOtp && storedUserData) {
//       navigate("/home");
//     }
//   }, [isAuthenticated, navigate]);

//   useEffect(() => {
//     document.body.style.backgroundColor = '#ffffff';
//     document.documentElement.style.backgroundColor = '#ffffff';

//     return () => {
//       document.body.style.backgroundColor = '';
//       document.documentElement.style.backgroundColor = '';
//     };
//   }, []);

//   const handleSubmit = async () => {
//     if (loginMethod === "phone") {
//       const trimmedPhoneNumber = phoneNo.trim();
      
//       if (!trimmedPhoneNumber) {
//         alert("Please enter a phone number.");
//         return;
//       }

//       if (trimmedPhoneNumber.length < 10) {
//         alert("Please enter a valid phone number.");
//         return;
//       }
    
//       const result = await dispatch(authenticate(trimmedPhoneNumber));
      
//       if (result.success) {
//         navigate("/verify", { 
//           state: { 
//             phoneNumber: trimmedPhoneNumber,
//             sentOtp: result.data 
//           }
//         });
//       } else {
//         alert(result.error?.message || "Authentication failed. Please try again.");
//       }
//     } else {
//       const trimmedServiceNo = serviceNo.trim();
//       const trimmedPassword = password.trim();
      
//       if (!trimmedServiceNo) {
//         alert("Please enter your service number.");
//         return;
//       }
      
//       if (!trimmedPassword) {
//         alert("Please enter your password.");
//         return;
//       }
      
//       const result = await dispatch(authenticateWithServiceNumber(trimmedServiceNo, trimmedPassword));
      
//       if (result.success) {
//         navigate("/home");
//       } else {
//         alert(result.error?.message || "Invalid service number or password. Please try again.");
//       }
//     }
//   };

//   const handleKeyPress = (e) => {
//     if (e.key === 'Enter') {
//       handleSubmit();
//     }
//   };

//   const handleClickShowPassword = () => {
//     setShowPassword(!showPassword);
//   };

//   return (
//     <Container maxWidth="xs" sx={{ py: { xs: 2, sm: 4 } }}>
//       {/* Header */}
//       <Box sx={{ mb: 4, textAlign: "center" }}>
//         <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: "#000" }}>
//           Welcome Back
//         </Typography>
//         <Typography variant="body2" sx={{ color: "#666" }}>
//           Sign in to continue to your account
//         </Typography>
//       </Box>

//       {/* Login Method Toggle */}
//       <Paper
//         elevation={0}
//         sx={{
//           border: "1px solid #e0e0e0",
//           borderRadius: 1,
//           p: 0.5,
//           mb: 3,
//           display: "flex",
//           background: "#f8f9fa"
//         }}
//       >
//         <Box
//           onClick={() => setLoginMethod("phone")}
//           sx={{
//             flex: 1,
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             gap: 1,
//             padding: "8px",
//             borderRadius: "6px",
//             background: loginMethod === "phone" ? "#1e0153" : "transparent",
//             cursor: "pointer",
//             transition: "all 0.2s ease",
//           }}
//         >
//           <Phone sx={{ 
//             fontSize: 18, 
//             color: loginMethod === "phone" ? "#fff" : "#666"
//           }} />
//           <Typography
//             sx={{
//               color: loginMethod === "phone" ? "#fff" : "#666",
//               fontSize: "0.875rem",
//               fontWeight: 500,
//             }}
//           >
//             Phone
//           </Typography>
//         </Box>

//         <Box
//           onClick={() => setLoginMethod("service")}
//           sx={{
//             flex: 1,
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             gap: 1,
//             padding: "8px",
//             borderRadius: "6px",
//             background: loginMethod === "service" ? "#1e0153" : "transparent",
//             cursor: "pointer",
//             transition: "all 0.2s ease",
//           }}
//         >
//           <Badge sx={{ 
//             fontSize: 18, 
//             color: loginMethod === "service" ? "#fff" : "#666"
//           }} />
//           <Typography
//             sx={{
//               color: loginMethod === "service" ? "#fff" : "#666",
//               fontSize: "0.875rem",
//               fontWeight: 500,
//             }}
//           >
//             Service No
//           </Typography>
//         </Box>
//       </Paper>

//       {/* Form */}
//       <Paper
//         elevation={0}
//         sx={{
//           border: "1px solid #e0e0e0",
//           borderRadius: 2,
//           p: 3,
//           mb: 3
//         }}
//       >
//         {error && (
//           <Alert 
//             severity="error" 
//             sx={{ 
//               mb: 2, 
//               borderRadius: 1,
//               "& .MuiAlert-icon": { fontSize: 20 }
//             }}
//           >
//             {error.message || "Authentication failed. Please try again."}
//           </Alert>
//         )}

//         {loginMethod === "phone" ? (
//           <Box>
//             <TextField
//               fullWidth
//               size="small"
//               placeholder="Enter mobile number"
//               value={phoneNo}
//               onChange={(e) => {
//                 const value = e.target.value.replace(/[^0-9]/g, '');
//                 setPhoneNumber(value);
//               }}
//               onKeyPress={handleKeyPress}
//               type="tel"
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <Phone sx={{ fontSize: 18, color: "#666" }} />
//                   </InputAdornment>
//                 ),
//               }}
//               sx={{
//                 mb: 2,
//                 "& .MuiOutlinedInput-root": {
//                   fontSize: "0.875rem",
//                   "&:hover fieldset": {
//                     borderColor: "#000",
//                   },
//                 },
//               }}
//             />
//           </Box>
//         ) : (
//           <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
//             <TextField
//               fullWidth
//               size="small"
//               placeholder="Service Number"
//               value={serviceNo}
//               onChange={(e) => setServiceNo(e.target.value)}
//               onKeyPress={handleKeyPress}
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <Badge sx={{ fontSize: 18, color: "#666" }} />
//                   </InputAdornment>
//                 ),
//               }}
//               sx={{
//                 "& .MuiOutlinedInput-root": {
//                   fontSize: "0.875rem",
//                   "&:hover fieldset": {
//                     borderColor: "#000",
//                   },
//                 },
//               }}
//             />

//             <TextField
//               fullWidth
//               size="small"
//               placeholder="Password"
//               type={showPassword ? "text" : "password"}
//               value={password}
//               onChange={(e) => setPassword(e.target.value)}
//               onKeyPress={handleKeyPress}
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <Lock sx={{ fontSize: 18, color: "#666" }} />
//                   </InputAdornment>
//                 ),
//                 endAdornment: (
//                   <InputAdornment position="end">
//                     <IconButton
//                       onClick={handleClickShowPassword}
//                       edge="end"
//                       size="small"
//                       sx={{ p: 0.5 }}
//                     >
//                       {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
//                     </IconButton>
//                   </InputAdornment>
//                 ),
//               }}
//               sx={{
//                 "& .MuiOutlinedInput-root": {
//                   fontSize: "0.875rem",
//                   "&:hover fieldset": {
//                     borderColor: "#000",
//                   },
//                 },
//               }}
//             />
//           </Box>
//         )}

//         {/* Remember Me Checkbox - Only for Service No login */}
//         {loginMethod === "service" && (
//           <FormControlLabel
//             control={
//               <Checkbox
//                 size="small"
//                 checked={rememberMe}
//                 onChange={(e) => setRememberMe(e.target.checked)}
//                 sx={{ p: 0.5, mr: 1 }}
//               />
//             }
//             label={
//               <Typography variant="caption" sx={{ color: "#666" }}>
//                 Remember me
//               </Typography>
//             }
//             sx={{ mb: 2 }}
//           />
//         )}

//         {/* Login Button */}
//         <LoadingButton
//           onClick={handleSubmit}
//           loading={loading}
//           variant="contained"
//           fullWidth
//           size="medium"
//           endIcon={<ArrowForward fontSize="small" />}
//           sx={{
//             bgcolor: "#1e0153",
//             color: "#fff",
//             borderRadius: 1,
//             py: 1,
//             fontSize: "0.875rem",
//             fontWeight: 500,
//             "&:hover": {
//               bgcolor: "#333",
//             },
//             "&.Mui-disabled": {
//               bgcolor: "#e0e0e0",
//               color: "#999",
//             },
//           }}
//         >
//           Sign In
//         </LoadingButton>
//       </Paper>

//       {/* Alternative Actions */}
//       <Paper
//         elevation={0}
//         sx={{
//           border: "1px solid #e0e0e0",
//           borderRadius: 2,
//           p: 2,
//           mb: 3
//         }}
//       >
//         <Typography variant="body2" sx={{ color: "#666", mb: 1, textAlign: "center" }}>
//           Don't have an account?
//         </Typography>
//         <Box
//           onClick={() => navigate("/register")}
//           sx={{
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             gap: 1,
//             padding: "10px",
//             borderRadius: 1,
//             background: "#f8f9fa",
//             border: "1px solid #e0e0e0",
//             cursor: "pointer",
//             transition: "all 0.2s ease",
//             "&:hover": {
//               background: "#f0f0f0",
//               borderColor: "#000",
//             },
//           }}
//         >
//           <PersonAdd sx={{ fontSize: 18, color: "#666" }} />
//           <Typography
//             sx={{
//               color: "#000",
//               fontSize: "0.875rem",
//               fontWeight: 500,
//             }}
//           >
//             Create new account
//           </Typography>
//         </Box>
//       </Paper>

//       {/* Footer Links */}
//       <Box sx={{ textAlign: "center" }}>
//         <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 0.5 }}>
//           Having trouble signing in?
//         </Typography>
//         <Link
//           sx={{
//             color: "#666",
//             fontSize: "0.75rem",
//             cursor: "pointer",
//             "&:hover": { color: "#000", textDecoration: "underline" }
//           }}
//         >
//           Reset password
//         </Link>
//         <Typography variant="caption" sx={{ color: "#999", mx: 1 }}>•</Typography>
//         <Link
//           sx={{
//             color: "#666",
//             fontSize: "0.75rem",
//             cursor: "pointer",
//             "&:hover": { color: "#000", textDecoration: "underline" }
//           }}
//         >
//           Contact support
//         </Link>
//       </Box>
//     </Container>
//   );
// };

// export default LoginPage;


















// import React, { useState, useEffect } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { authenticate } from "../actions/authActions";
// import { Box, Container, Typography, TextField, InputAdornment, IconButton } from "@mui/material";
// import LoadingButton from "@mui/lab/LoadingButton";
// import { useNavigate } from "react-router-dom";
// import { Phone, ArrowForward, PersonAdd } from "@mui/icons-material";

// const LoginPage = () => {
//   const [phoneNo, setPhoneNumber] = useState("");
//   const dispatch = useDispatch();
//   const { loading, error, isAuthenticated } = useSelector((state) => state.auth);
//   const navigate = useNavigate();

//   useEffect(() => {
//     const storedPhoneNumber = localStorage.getItem("phoneNumber");
//     const storedOtp = localStorage.getItem("sentOtp");
//     const storedUserData = localStorage.getItem("userData");
    
//     if (isAuthenticated && storedPhoneNumber && storedOtp && storedUserData) {
//       navigate("/home");
//     }
//   }, [isAuthenticated, navigate]);

//   useEffect(() => {
//     document.body.style.margin = '0';
//     document.body.style.padding = '0';
//     document.body.style.backgroundColor = '#0f172a';
//     document.documentElement.style.margin = '0';
//     document.documentElement.style.padding = '0';
//     document.documentElement.style.backgroundColor = '#0f172a';

//     return () => {
//       document.body.style.margin = '';
//       document.body.style.padding = '';
//       document.body.style.backgroundColor = '';
//       document.documentElement.style.margin = '';
//       document.documentElement.style.padding = '';
//       document.documentElement.style.backgroundColor = '';
//     };
//   }, []);

//   const handleSubmit = async () => {
//     const trimmedPhoneNumber = phoneNo.trim();
    
//     if (!trimmedPhoneNumber) {
//       alert("Please enter a phone number.");
//       return;
//     }

//     if (trimmedPhoneNumber.length < 10) {
//       alert("Please enter a valid phone number.");
//       return;
//     }
  
//     const result = await dispatch(authenticate(trimmedPhoneNumber));
    
//     if (result.success) {
//       navigate("/verify", { 
//         state: { 
//           phoneNumber: trimmedPhoneNumber,
//           sentOtp: result.data 
//         }
//       });
//     } else {
//       alert(result.error?.message || "Authentication failed. Please try again.");
//     }
//   };

//   const handleKeyPress = (e) => {
//     if (e.key === 'Enter') {
//       handleSubmit();
//     }
//   };
  
//   return (
//     <Box
//       sx={{
//         minHeight: "100vh",
//         width: "100vw",
//         background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         position: "fixed",
//         top: 0,
//         left: 0,
//         right: 0,
//         bottom: 0,
//         margin: 0,
//         padding: 0,
//         overflow: "hidden",
//       }}
//     >
//       {/* Animated Background Elements */}
//       <Box
//         sx={{
//           position: "absolute",
//           top: "-10%",
//           right: "-5%",
//           width: "500px",
//           height: "500px",
//           borderRadius: "50%",
//           background: "radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)",
//           animation: "float 20s ease-in-out infinite",
//           "@keyframes float": {
//             "0%, 100%": { transform: "translate(0, 0) scale(1)" },
//             "50%": { transform: "translate(-50px, 50px) scale(1.1)" },
//           },
//         }}
//       />
//       <Box
//         sx={{
//           position: "absolute",
//           bottom: "-15%",
//           left: "-10%",
//           width: "600px",
//           height: "600px",
//           borderRadius: "50%",
//           background: "radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)",
//           animation: "float 25s ease-in-out infinite reverse",
//         }}
//       />

//       {/* Glassmorphism Grid Pattern */}
//       <Box
//         sx={{
//           position: "absolute",
//           inset: 0,
//           backgroundImage: `
//             linear-gradient(rgba(148, 163, 184, 0.03) 1px, transparent 1px),
//             linear-gradient(90deg, rgba(148, 163, 184, 0.03) 1px, transparent 1px)
//           `,
//           backgroundSize: "50px 50px",
//           zIndex: 0,
//         }}
//       />

//       <Container
//         maxWidth="sm"
//         sx={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           padding: { xs: 2, sm: 3 },
//           zIndex: 1,
//         }}
//       >
//         <Box
//           sx={{
//             width: "100%",
//             maxWidth: "450px",
//             background: "rgba(255, 255, 255, 0.05)",
//             backdropFilter: "blur(20px)",
//             borderRadius: "24px",
//             border: "1px solid rgba(255, 255, 255, 0.1)",
//             boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
//             padding: { xs: 3, sm: 5 },
//             position: "relative",
//             overflow: "hidden",
//             transition: "all 0.3s ease",
//             "&:hover": {
//               transform: "translateY(-5px)",
//               boxShadow: "0 30px 60px -15px rgba(0, 0, 0, 0.6)",
//               border: "1px solid rgba(255, 255, 255, 0.15)",
//             },
//           }}
//         >
//           {/* Top Gradient Accent */}
//           <Box
//             sx={{
//               position: "absolute",
//               top: 0,
//               left: 0,
//               right: 0,
//               height: "4px",
//               background: "linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)",
//             }}
//           />

//           {/* Header Section */}
//           <Box textAlign="center" mb={4}>
//             <Box
//               sx={{
//                 display: "inline-flex",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 width: "80px",
//                 height: "80px",
//                 borderRadius: "20px",
//                 background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
//                 boxShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
//                 mb: 3,
//                 position: "relative",
//                 "&::before": {
//                   content: '""',
//                   position: "absolute",
//                   inset: "-2px",
//                   borderRadius: "20px",
//                   background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
//                   opacity: 0.5,
//                   filter: "blur(10px)",
//                   zIndex: -1,
//                 },
//               }}
//             >
//               <Phone sx={{ fontSize: 40, color: "#fff" }} />
//             </Box>

//             <Typography
//               variant="h4"
//               sx={{
//                 fontWeight: 700,
//                 color: "#fff",
//                 mb: 1,
//                 fontSize: { xs: "1.75rem", sm: "2.125rem" },
//                 letterSpacing: "-0.02em",
//               }}
//             >
//               Welcome Back
//             </Typography>
//             <Typography
//               variant="body1"
//               sx={{
//                 color: "rgba(255, 255, 255, 0.6)",
//                 fontSize: { xs: "0.95rem", sm: "1rem" },
//                 fontWeight: 400,
//               }}
//             >
//               Enter your mobile number to continue
//             </Typography>
//           </Box>

//           {/* Phone Input */}
//           <Box mb={3}>
//             <TextField
//               fullWidth
//               placeholder="Enter mobile number"
//               value={phoneNo}
//               onChange={(e) => {
//                 const value = e.target.value.replace(/[^0-9]/g, '');
//                 setPhoneNumber(value);
//               }}
//               onKeyPress={handleKeyPress}
//               type="tel"
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <Phone sx={{ color: "rgba(255, 255, 255, 0.5)" }} />
//                   </InputAdornment>
//                 ),
//               }}
//               sx={{
//                 "& .MuiOutlinedInput-root": {
//                   backgroundColor: "rgba(255, 255, 255, 0.05)",
//                   borderRadius: "12px",
//                   height: "56px",
//                   fontSize: "1rem",
//                   color: "#fff",
//                   border: "1px solid rgba(255, 255, 255, 0.1)",
//                   transition: "all 0.3s ease",
//                   "& fieldset": {
//                     border: "none",
//                   },
//                   "&:hover": {
//                     backgroundColor: "rgba(255, 255, 255, 0.08)",
//                     border: "1px solid rgba(255, 255, 255, 0.2)",
//                   },
//                   "&.Mui-focused": {
//                     backgroundColor: "rgba(255, 255, 255, 0.08)",
//                     border: "1px solid rgba(59, 130, 246, 0.5)",
//                     boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.1)",
//                   },
//                 },
//                 "& .MuiOutlinedInput-input": {
//                   "&::placeholder": {
//                     color: "rgba(255, 255, 255, 0.4)",
//                     opacity: 1,
//                   },
//                 },
//               }}
//             />
//           </Box>

//           {/* Submit Button */}
//           <LoadingButton
//             onClick={handleSubmit}
//             loading={loading}
//             fullWidth
//             endIcon={<ArrowForward />}
//             sx={{
//               height: "56px",
//               borderRadius: "12px",
//               background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
//               color: "#fff",
//               fontSize: "1rem",
//               fontWeight: 600,
//               textTransform: "none",
//               boxShadow: "0 10px 30px rgba(59, 130, 246, 0.3)",
//               mb: 3,
//               position: "relative",
//               overflow: "hidden",
//               "&::before": {
//                 content: '""',
//                 position: "absolute",
//                 top: 0,
//                 left: "-100%",
//                 width: "100%",
//                 height: "100%",
//                 background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
//                 transition: "left 0.5s ease",
//               },
//               "&:hover": {
//                 background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
//                 boxShadow: "0 15px 40px rgba(59, 130, 246, 0.4)",
//                 transform: "translateY(-2px)",
//                 "&::before": {
//                   left: "100%",
//                 },
//               },
//               "&:active": {
//                 transform: "translateY(0)",
//               },
//               "&.Mui-disabled": {
//                 background: "rgba(255, 255, 255, 0.1)",
//                 color: "rgba(255, 255, 255, 0.4)",
//               },
//             }}
//           >
//             Continue
//           </LoadingButton>

//           {/* Divider */}
//           <Box
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               mb: 3,
//             }}
//           >
//             <Box sx={{ flex: 1, height: "1px", background: "rgba(255, 255, 255, 0.1)" }} />
//             <Typography
//               sx={{
//                 px: 2,
//                 color: "rgba(255, 255, 255, 0.4)",
//                 fontSize: "0.875rem",
//                 fontWeight: 500,
//               }}
//             >
//               OR
//             </Typography>
//             <Box sx={{ flex: 1, height: "1px", background: "rgba(255, 255, 255, 0.1)" }} />
//           </Box>

//           {/* Register Link */}
//           <Box
//             onClick={() => navigate("/register")}
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               gap: 1,
//               padding: "14px",
//               borderRadius: "12px",
//               background: "rgba(255, 255, 255, 0.03)",
//               border: "1px solid rgba(255, 255, 255, 0.1)",
//               cursor: "pointer",
//               transition: "all 0.3s ease",
//               "&:hover": {
//                 background: "rgba(255, 255, 255, 0.06)",
//                 border: "1px solid rgba(255, 255, 255, 0.15)",
//                 transform: "translateY(-2px)",
//               },
//             }}
//           >
//             <PersonAdd sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: 20 }} />
//             <Typography
//               sx={{
//                 color: "rgba(255, 255, 255, 0.7)",
//                 fontSize: "0.95rem",
//                 fontWeight: 500,
//               }}
//             >
//               Create new account
//             </Typography>
//           </Box>

//           {/* Error Message */}
//           {error && (
//             <Box
//               sx={{
//                 mt: 3,
//                 padding: "12px 16px",
//                 borderRadius: "12px",
//                 background: "rgba(239, 68, 68, 0.1)",
//                 border: "1px solid rgba(239, 68, 68, 0.3)",
//               }}
//             >
//               <Typography
//                 sx={{
//                   color: "#fca5a5",
//                   fontSize: "0.875rem",
//                   textAlign: "center",
//                 }}
//               >
//                 {error.message || "An error occurred. Please try again."}
//               </Typography>
//             </Box>
//           )}
//         </Box>
//       </Container>
//     </Box>
//   );
// };

// export default LoginPage;